/**
 * Campaign parameters of a visit: remembered from the url of any page (`storeUtmFromUrl`), sent with the lead
 * (`readUtm`) and forgotten once the lead is sent (`clearStoredUtm`).
 */
export type UtmStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const UTM_KEYS = ['utm_source', 'utm_campaign'] as const;
const STORED_KEYS = [...UTM_KEYS, 'qwp_id'] as const;

/** Remembers the parameters of the url for the next pages, as every Tryba page does on load. */
export function storeUtmFromUrl(search: string, storage: UtmStorage): void {
    const parameters = new URLSearchParams(search);
    for (const key of STORED_KEYS) {
        const value = parameters.get(key);
        if (value !== null) {
            attempt(() => storage.setItem(key, value));
        }
    }
}

/**
 * The utm of the url when it has some, whatever other parameters it carries; otherwise the ones remembered from an
 * earlier page. The two are never mixed: a new campaign does not inherit the source of an older visit.
 */
export function readUtm(search: string, storage: UtmStorage): Record<string, string> {
    const parameters = new URLSearchParams(search);
    const fromUrl = UTM_KEYS.some((key) => parameters.has(key));

    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
        const value = fromUrl ? parameters.get(key) : attempt(() => storage.getItem(key));
        if (typeof value === 'string' && value !== '') {
            utm[key] = value;
        }
    }

    return utm;
}

/** Forgets the campaign once the lead is sent, so that it is not credited with the next ones. */
export function clearStoredUtm(storage: UtmStorage): void {
    for (const key of STORED_KEYS) {
        attempt(() => storage.removeItem(key));
    }
}

/** The storage can be missing or refuse access (private browsing, blocked site data): the campaign is then lost, never the lead. */
function attempt<T>(action: () => T): T | null {
    try {
        return action();
    } catch {
        return null;
    }
}
